import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount } from "../../lib/auth.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { runAz, rethrowIfAzUnavailable } from "../../lib/az.js";
import { enroll } from "../../lib/telemetry-enroll.js";
import { discoverGateway } from "../../lib/discovery.js";

const INGEST_KEY_SECRET = "m8t-ingest-key";

/**
 * Resolve the customer Key Vault the same way the gateway itself does: read
 * KEY_VAULT_URI off the gateway Container App's env (see
 * apps/web/lib/gateway/telemetry/identity.ts, which reads `m8t-ingest-key`
 * from that exact vault). `DiscoveryResult` has no `keyVaultUri` field, so
 * this goes straight to the live Container App instead of inventing one.
 */
async function resolveKeyVaultName(containerAppResourceId: string): Promise<string> {
  const kvUri = (
    await runAz([
      "containerapp", "show", "--ids", containerAppResourceId,
      "--query", "properties.template.containers[0].env[?name=='KEY_VAULT_URI'].value | [0]",
      "-o", "tsv",
    ])
  ).trim();
  if (!kvUri) {
    throw new LocalCliError({
      code: "TELEMETRY_ENROLL_NO_KV",
      message: "Could not resolve the platform Key Vault: the gateway Container App has no KEY_VAULT_URI env var.",
      hint: "Verify the deployment via 'az containerapp show --ids <id> --query properties.template.containers[0].env'.",
    });
  }
  const host = new URL(kvUri).hostname;
  const name = host.split(".")[0];
  if (!name) {
    throw new LocalCliError({ code: "TELEMETRY_ENROLL_BAD_KV_URI", message: `Could not parse a Key Vault name from KEY_VAULT_URI '${kvUri}'.` });
  }
  return name;
}

export class TelemetryEnrollCommand extends M8tCommand {
  static paths = [["telemetry", "enroll"]];
  static usage = Command.Usage({
    description: "Enroll this installation for operational telemetry (pre-existing installs).",
    details:
      "Generates an instance id + ingest key from the m8t telemetry ingest and stores the key in your platform Key Vault, the same place the installer writes it on a fresh install. Your installation is identified only by that random instance id — no contact, company, or subscription details are sent unless you pass them explicitly. Idempotent: refuses if a key already exists.",
    examples: [
      ["Enroll", "$0 telemetry enroll"],
      ["Enroll and share a support contact", "$0 telemetry enroll --contact-email you@example.com --company 'Acme'"],
    ],
  });

  company = Option.String("--company", { description: "Share your company name with m8t support. Not sent unless you pass it." });
  contactEmail = Option.String("--contact-email", { description: "Share a contact email with m8t support. Not sent unless you pass it." });
  subscription = Option.String("--subscription", { description: "Azure subscription id used to find your deployment. Never sent to m8t." });
  resourceGroup = Option.String("--resource-group", { description: "Resource group to disambiguate discovery, if you have more than one m8t deployment." });
  force = Option.Boolean("--force", false, { description: "Enroll even when a key is already stored. Use only when m8t support asks you to." });

  async executeCommand(): Promise<number> {
    const account = await getAzAccount();
    // Discovery-only: which subscription to look in. This value does NOT reach the
    // enrollment body — subscription ids are not sent.
    const subscriptionId = (typeof this.subscription === "string" ? this.subscription : undefined) ?? account.subscriptionId;
    // Strictly opt-in. The signed-in UPN is deliberately NOT a fallback: a contact
    // address we were never asked to send is exactly the default this replaced.
    const contactEmail = typeof this.contactEmail === "string" ? this.contactEmail : undefined;
    const company = typeof this.company === "string" ? this.company : undefined;

    const d = await discoverGateway({
      subscriptionId,
      interactive: false,
      resourceGroup: typeof this.resourceGroup === "string" ? this.resourceGroup : undefined,
    });
    const kvName = await resolveKeyVaultName(d.containerAppResourceId);

    if (this.force !== true) {
      let existing = "";
      try {
        existing = await runAz(["keyvault", "secret", "show", "--vault-name", kvName, "--name", INGEST_KEY_SECRET, "--query", "value", "-o", "tsv"]);
      } catch (e) {
        rethrowIfAzUnavailable(e);
        existing = ""; // absent — proceed with enrollment
      }
      if (existing.trim()) {
        const wantedContact = contactEmail !== undefined || company !== undefined;
        throw new LocalCliError({
          code: "TELEMETRY_ALREADY_ENROLLED",
          message: wantedContact
            ? "This installation is already enrolled, so contact details can't be attached from here."
            : "This installation is already enrolled.",
          hint: "To add or change the contact details on your record, reach out through the channels in SUPPORT.md and we'll update it for you.",
        });
      }
    }

    const { instanceId, ingestKey } = await enroll({
      ...(company ? { company } : {}),
      ...(contactEmail ? { contactEmail } : {}),
    });
    // The ingest key is long-lived (it authenticates all telemetry for this
    // installation until revoked), so it must never ride in `az`'s argv: any
    // other process on the machine can read a process's argument vector from
    // the process table for as long as `az` is running. Write it to a
    // restrictive-at-creation temp file instead and have `az` read it via
    // `--file` — mirrors installer/lib.sh's telemetry_persist_key. The temp
    // dir comes from fs.mkdtempSync, which is collision-proof across
    // concurrent runs; the key file is written with mode 0o600 at creation
    // (no separate chmod, so there is no window where it is world-readable)
    // and with no trailing newline. Removed on every exit path, including
    // when the `az` call throws.
    const keyDir = fs.mkdtempSync(path.join(os.tmpdir(), "m8t-ingest-key-"));
    const keyFile = path.join(keyDir, "key");
    try {
      fs.writeFileSync(keyFile, ingestKey, { mode: 0o600 });
      await runAz(["keyvault", "secret", "set", "--vault-name", kvName, "--name", INGEST_KEY_SECRET, "--file", keyFile, "--only-show-errors"]);
    } finally {
      fs.rmSync(keyDir, { recursive: true, force: true });
    }

    this.context.stdout.write(
      `${colors.success("✓")} enrolled (instance ${instanceId}); ingest key stored in Key Vault ${kvName}. The gateway picks it up on its next cycle.\n`,
    );
    return 0;
  }
}
